import { gql } from 'graphql-request';

export interface GetFleetDataVars {
  appId: string;
}

export const GET_FLEET_DATA = gql`
  query getFleetData($appId: String) {
    allFleets(appId: $appId) {
      currentYear
      currentWeek
      fleets {
        clientId
        projectId
        workflowId
        fleetId
        appId
        jobId
        batteryCount
        vehicleCount
        gatewayCount
        gateways {
          gatewaySerial
          createdAt
          updatedAt
          status {
            status
          }
        }
        warningsCurrentWeek
        criticalsCurrentWeek
      }
    }
  }
`;

export const GET_ANALYSIS_JOB_LIST = gql`
  query getAnalysisJobList($appId: String, $jobId: String) {
    analysisJobList(appId: $appId, jobId: $jobId) {
      app_id
      device_id
      fleet_asset_id
      end_date
      job_id
      job_type
      last_run
      start_date
      update_frequency
    }
  }
`;

export type GetSummaryDataVars = {
  appId?: string;
  fleetJobId?: string;
  jobIds?: string[];
  startDate?: string;
  endDate?: string;
};

export const GET_SUMMARY_DATA = gql`
  query getSummaryJob(
    $appId: String
    $fleetJobId: String
    $jobIds: [String]
    $startDate: String
    $endDate: String
  ) {
    summaryJob(
      appId: $appId
      fleetJobId: $fleetJobId
      jobIds: $jobIds
      startDate: $startDate
      endDate: $endDate
    ) {
      app_id
      job_id
      date
      power_logger_name
      key_time
      charge_time
      discharge_time
      utilization_rate
      charge_volume
      discharge_volume
      min_voltage
      max_voltage
      min_temperature
      max_temperature
      avg_temperature
      electrolyte_status
      min_load_voltage
      max_load_voltage
      avg_load_voltage
      telemetry_last_update
      max_soc
      min_soc
      starting_soc
      finish_soc
      finished
      equalized
      plug_ins
      finish_charging_time
      finish_eq_time
      max_current
      last_timestamp
      prev_timestamp
      temperature_over_threshold_time
      created_at
      updated_at
      energy_over_warning_time
      energy_over_critical_time
      avg_hour
      max_hour
      max_charge_current
      peaks_over_40
      peaks_over_60
    }
  }
`;

export type GetFleetDetailVars = {
  deviceId?: string;
  assetId?: string;
  dates?: string[];
  timeZoneOffset?: number;
};

export const GET_FLEET_DETAIL = gql`
  query getFleetDetail(
    $deviceId: String
    $assetId: String
    $dates: [String!]
    $timeZoneOffset: Int
  ) {
    loggerDayDetailsLocalized(
      deviceId: $deviceId
      assetId: $assetId
      dates: $dates
      timeZoneOffset: $timeZoneOffset
    ) {
      deviceID
      date
      timeSeries {
        timestamp
        eventType
        eventID
        current
        voltage
        temperature
        electrolyteStatus
      }
      events {
        timestamp
        eventCode
        transactionID
        duration
        ampHours
        chargeCycle
        startTemp
        endTemp
        startVolts
        endVolts
        elLowAtStart
        elLowAtEnd
        finished
        equalized
        diagnostic
        maxTemp
        maxTempTimeStamp
        minTemp
        minTempTimeStamp
        maxVolts
        maxVoltsTimeStamp
        minVolts
        minVoltsTimeStamp
        maxCurrent
        maxCurrentTimeStamp
        min2SecVolts
        min10SecVolts
        totalFinishTime
        maxFinishTime
        soc
      }
    }
  }
`;

export interface GetFleetAnalysisVars {
  clientId: number;
  projectId: number;
  workflowId: number;
}

export const GET_FLEET_ANALYSIS = gql`
  query getFleetSummary($clientId: Int, $projectId: Int, $workflowId: Int) {
    fleetSummary(clientId: $clientId, projectId: $projectId, workflowId: $workflowId) {
      clientId
      projectId
      workflowId
      appId
      jobId
      details {
        batteries {
          chargingStrategy
          count
        }
      }
      batteryList {
        fleetAssetId
        assetTemplateId
        analysisJobs {
          analysisJobId
          assetName
          lastRun
          lastTelemetryUpdate
          alertConfigurationId
        }
        nominalCapacity
        nominalVoltage
        chargerType
        dateEnteredService
        seriesName
        modelName
        customerId
        manufacturerId
        role
        zone
      }
      vehicleList {
        fleetAssetId
        assetTemplateId
        analysisJobs {
          analysisJobId
          assetName
          lastRun
          lastTelemetryUpdate
          alertConfigurationId
        }
        nominalCapacity
        nominalVoltage
        chargerType
        dateEnteredService
        capacity
        cableConfiguration
        nominalCapacity
        nominalVoltage
        chargerType
        vehicleType
        vehicleMake
        vehicleModel
        hoursPerYear
        customerId
        manufacturerId
        dateEnteredService
        role
        zone
      }
      gatewayList {
        fleetAssetId
        assetTemplateId
        analysisJobs {
          analysisJobId
          assetName
          lastRun
          lastTelemetryUpdate
          alertConfigurationId
        }
        customerId
        manufacturerId
        dateEnteredService
        location
      }
      chargerList {
        fleetAssetId
        assetTemplateId
        analysisJobs {
          analysisJobId
          assetName
          lastRun
          lastTelemetryUpdate
          alertConfigurationId
        }
        customerId
        manufacturerId
        chargerType
        chargerSeries
        chargerModel
      }
      FleetAggregationDetails {
        alertMonths {
          year
          month
          jobId
          powerLoggerName
          alertSettingId
          alertLevel
          count
        }
        alertWeeks {
          year
          week
          jobId
          powerLoggerName
          alertSettingId
          alertLevel
          count
        }
        alertYears {
          year
          jobId
          powerLoggerName
          alertSettingId
          alertLevel
          count
        }
        months {
          year
          month
          jobId
          powerLoggerName
          dischargeVolume
          maxDischargeVolume
          electrolyteStatus
        }
        weeks {
          year
          week
          jobId
          powerLoggerName
          dischargeVolume
          maxDischargeVolume
          electrolyteStatus
        }
        years {
          year
          jobId
          powerLoggerName
          dischargeVolume
          maxDischargeVolume
          electrolyteStatus
        }
      }
    }
  }
`;

export interface GetCustomerDataVars {
  appId: string;
}

export const GET_CUSTOMER_DATA = gql`
  query getCustomerData($appId: String) {
    customerData(appId: $appId) {
      currentYear
      currentWeek
      accounts {
        clientId
        parentClientId
        portalAccountId
        fleetId
        parentPortalAccountId
        batteryCount
        monitoredBatteryCount
        batteryCountStartOfMonth
        monitoredBatteryCountStartOfMonth
        chargerCount
        chargerCountStartOfMonth
        gatewayCount
        energyUsage
        energyUsageLastWeek
        batteriesWithIssuesCount
        projectId
        unexpectedAssets
        unlicensedAssets
        totalLicenses
        unassignedLicenses
        firmwareIssues
        issues {
          issueType
          alertSettingId
          alertLevel
          count
        }
        issuesLastWeek {
          issueType
          alertSettingId
          alertLevel
          count
        }
      }
    }
  }
`;

export interface GetNewFleetDataVars {
  clientId: number;
  projectId: number;
  workflowId: number;
  fleetId: number;
  homeAccountId: number;
}

export const GET_NEW_FLEET_DATA = gql`
  query getNewFleetData(
    $clientId: Int
    $projectId: Int
    $workflowId: Int
    $fleetId: Int
    $homeAccountId: Int
  ) {
    fleetData(
      clientId: $clientId
      projectId: $projectId
      workflowId: $workflowId
      fleetId: $fleetId
      homeAccountId: $homeAccountId
    ) {
      currentYear
      currentMonth
      currentWeek
      isDemo
      dashboard {
        facilityType
        customerName
        facilityName
        userCount
        monitoredBatteryCount
        gateways {
          lastDataUpdate
          status
          name
          zoneName
          temperature {
            now {
              number
              type
            }
            weeklyAverage {
              number
              type
            }
          }
        }
        batteryGroups {
          location
          batteryCount
          batteryCountLastWeek
          stats {
            good {
              count
              countLastWeek
              weeklyChange
            }
            atRisk {
              count
              countLastWeek
              weeklyChange
            }
            noData {
              count
              countLastWeek
              weeklyChange
            }
            critical {
              count
              countLastWeek
              weeklyChange
            }
          }
        }
        issuesTable {
          assetId
          model
          serial
          alias
          currentHealth
          plugIns
          manufacturer
          capacity
          voltage
          issues {
            operational {
              equalizations
              electrolyteLevel
              timeOnCharge
              minimumStateOfCharge
            }
            systemic {
              energyThroughput
              minimumLoadVoltage
              highTemp
              lowTemp
              firmwareVersion
            }
          }
        }
        fleet {
          overview {
            fleetSize
            weeklyChange
          }
          health {
            good {
              count
              countLastWeek
              weeklyChange
            }
            atRisk {
              count
              countLastWeek
              weeklyChange
            }
            critical {
              count
              countLastWeek
              weeklyChange
            }
            noData {
              count
              countLastWeek
              weeklyChange
            }
          }
        }
      }
      fleetHealth {
        fleetHealth {
          good {
            count
            countLastWeek
            weeklyChange
          }
          atRisk {
            count
            countLastWeek
            weeklyChange
          }
          critical {
            count
            countLastWeek
            weeklyChange
          }
          noData {
            count
            countLastWeek
            weeklyChange
          }
        }
        batteryCount {
          count
          countLastWeek
          weeklyChange
        }
        monthlyTable {
          year
          month
          alias
          capacity {
            AHr
            kWh
          }
          chargingType
          currentHealth
          issues {
            operational {
              equalizations
              electrolyteLevel
              timeOnCharge
              minimumStateOfCharge
            }
            systemic {
              energyThroughput
              minimumLoadVoltage
              lowTemp
              highTemp
              firmwareVersion
            }
          }
          model
          avgDailyPlugins
          serial
          serviceEntryDate
          type
          vehicle
          voltage
          zones
        }
        monthlySummary {
          id
          year
          month
          issues {
            operational {
              total
              equalizations {
                atRisk
                critical
                total
              }
              electrolyteLevel {
                atRisk
                critical
                total
              }
              timeOnCharge {
                atRisk
                critical
                total
              }
              minimumStateOfCharge {
                atRisk
                critical
                total
              }
            }
            systemic {
              total
              energyThroughput {
                atRisk
                critical
                total
              }
              minimumLoadVoltage {
                atRisk
                critical
                total
              }
              highTemp {
                atRisk
                critical
                total
              }
              lowTemp {
                atRisk
                critical
                total
              }
              firmwareVersion {
                atRisk
                critical
                total
              }
            }
          }
        }
        currentIssues {
          operational {
            count
            countLastWeek
            weeklyChange
          }
          systemic {
            count
            countLastWeek
            weeklyChange
          }
        }
        adminIssues {
          unexpectedAssets
          unlicensedAssets
          firmwareIssues
        }
      }
      energyManagement {
        batteryTable {
          year
          month
          alias
          capacity {
            AHr
            kWh
          }
          chargingType
          cycleLife
          entryDate
          highestUsageDay {
            aHR
            percent
          }
          lastCommunicatedDaysAgo
          lifetimeAHr
          model
          remainingLife
          serial
          type
          usage
          voltage
          zones
        }
        currentFleet {
          batteryFleetSize {
            count
            countLastMonth
            monthlyChange
          }
          storedNominalCapacity {
            percentChange
            total
          }
        }
        currentMonthlyEnergyUsage {
          averageDay {
            percentOfCapacity
            total
          }
          peakDay {
            date
            megawattHours
            percentOfCapacity
          }
          totalEnergyUsage {
            percentChange
            total
          }
        }
        energyUsageChart {
          year
          months {
            month
            energyUsage
            batteries
            storedNominalCapacity
          }
        }
      }
      assetManagement {
        monitoredBatteryCount {
          count
          countLastWeek
          weeklyChange
        }
        ignoredBatteryCount
        unexpectedCount
        unlicensedCount
        firmwareIssuesCount
        monitoredBatteries {
          serial
          alias
          model
          powerLoggerSerial
          firmwareUpToDate
          licensed
          healthStatus
          lastSeen
          seenBy
          serviceEntryDate
        }
      }
      batteryDetailIssues {
        batterySerial
        issueType
        alertLevel
        count
        year
        week
      }
    }
  }
`;

export const GET_CLIENTS_SUMMARY = gql`
  query ClientsSummary($page: Int, $perPage: Int, $search: ClientsSummarySearchInput) {
    clientsSummary(pagination: { page: $page, perPage: $perPage }, search: $search) {
      meta {
        currentPage
        perPage
        totalEntries
        totalPages
      }
      results {
        id
        name
        account_number
        client_type
        channel_type
        total_fleet_size
        is_archived
        facility_count
        project_count
        client_count
        owner
        created_at
        last_updated
      }
    }
  }
`;

export const GET_FLEET_AGGREGATIONS_BY_JOB_BY_TIME_PERIOD = gql`
  query FleetAggregationsByJobByTimePeriod(
    $appId: String!
    $jobId: String!
    $type: FleetAggregationType!
    $period: FleetAggregationPeriod!
  ) {
    fleetAggregationByJobTimePeriod(app_id: $appId, job_id: $jobId, type: $type, period: $period) {
      app_id
      job_id
      power_logger_name
      alert_setting_id
      alert_level
      sub_type_1
      sub_type_2
      sub_type_3
      sub_type_4
      sub_type_5
      sub_type_6
      sub_type_7
      sub_type_8
      sub_type_9
      sub_type_10
      charge_time
      max_charge_time
      discharge_time
      max_discharge_time
      discharge_volume
      discharge_volume_over_threshold
      max_discharge_volume
      charge_volume
      max_charge_volume
      min_voltage
      max_voltage
      min_temperature
      max_temperature
      avg_temperature
      min_load_voltage
      max_load_voltage
      avg_load_voltage
      max_soc
      min_soc
      max_current
      temperature_over_threshold_time
      energy_over_warning_time
      energy_over_critical_time
      avg_hour
      max_hour
      electrolyte_status
      plug_ins
      days
      count
      year
      month
      week
    }
  }
`;

export const GET_DEVICE_LAST_UPDATE_LIST = gql`
  query DeviceLastUpdateList($deviceIds: [String!]!) {
    deviceLastUpdateList(deviceIds: $deviceIds) {
      deviceID
      lastUpdate
    }
  }
`;

export const GET_ALL_DEVICES_LAST_UPDATE = gql`
  query allDevicesLastUpdate {
    allDevicesLastUpdate {
      deviceID
      lastUpdate
    }
  }
`;
